import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Modal from '../../custom/Modal/Modal';
import {
	User,
	Time,
	Smile,
	Check,
	Info,
	MedicalEarPhone,
	Bag,
	Attach,
	Search,
	Download,
	Exit,
	BookAppointment
} from '../../../Assets/SVGS';
import { doctors } from '../../../Pages/Website/Doctors/doctorsData';
import downloadImage from '../../../utils/downloadImage';
import './DoctorsSection.css';

function DoctorsSection() {
	const { t, i18n } = useTranslation();
	const [activeFilter, setActiveFilter] = useState('all');
	const [selectedDoctor, setSelectedDoctor] = useState(null);
	const [previewCert, setPreviewCert] = useState(null);

	const pick = (obj) => obj?.[i18n.language] ?? obj?.en ?? obj?.ar;

	const specialties = useMemo(() => [...new Set(doctors.map((d) => pick(d.specialty)))], [i18n.language]);

	const filteredDoctors = useMemo(
		() => (activeFilter === 'all' ? doctors : doctors.filter((d) => pick(d.specialty) === activeFilter)),
		[activeFilter, i18n.language]
	);

	const totalCertificates = doctors.reduce((sum, d) => sum + d.certificates.length, 0);
	const totalAchievements = doctors.reduce((sum, d) => sum + d.achievements.length, 0);
	const maxExperience = Math.max(...doctors.map((d) => d.experienceYears));

	return (
		<>
			<section className='doctors-section'>
				<div className='content'>
					<h2 className='h-after-effect'>{t('ourDoctors')}</h2>
					<p className='doctors-intro'>{t('doctorsIntro')}</p>

					<div className='doctors-stats'>
						<div>
							<h3>{doctors.length}+</h3>
							<span>{t('doctors')}</span>
						</div>
						<div>
							<h3>{maxExperience}+</h3>
							<span>{t('yearsOfExperience')}</span>
						</div>
						<div>
							<h3>{totalCertificates}</h3>
							<span>{t('certificates')}</span>
						</div>
						<div>
							<h3>{totalAchievements}+</h3>
							<span>{t('achievements')}</span>
						</div>
					</div>

					<div className='doctors-filters'>
						<button
							className={activeFilter === 'all' ? 'chip active' : 'chip'}
							onClick={() => setActiveFilter('all')}
						>
							{t('allDoctors')}
						</button>
						{specialties.map((s) => (
							<button
								key={s}
								className={activeFilter === s ? 'chip active' : 'chip'}
								onClick={() => setActiveFilter(s)}
							>
								{s}
							</button>
						))}
					</div>

					<div className='doctors-grid'>
						{filteredDoctors.map((doc) => (
							<article className='doctor-card' key={doc.id}>
								<div className='card-img'>
									<img src={doc.image} alt={pick(doc.name)} loading='lazy' />
									<span className='badge'>{pick(doc.specialty)}</span>
								</div>
								<div className='card-body'>
									<h3>{pick(doc.name)}</h3>
									<p className='excerpt'>{pick(doc.brief)}</p>
									<div className='card-meta'>
										<span>
											<User width='16px' />
											{doc.age} {t('age')}
										</span>
										<span>
											<Time width='16px' />
											{doc.experienceYears}+
										</span>
										<span>
											<Smile width='16px' />
											{doc.rating}
										</span>
									</div>
									<button className='view-btn' onClick={() => setSelectedDoctor(doc)}>
										<User width='18px' />
										{t('viewProfile')}
									</button>
								</div>
							</article>
						))}
					</div>
				</div>

				<Modal isOpen={!!selectedDoctor} handleCancel={() => setSelectedDoctor(null)}>
					{selectedDoctor && (
						<div className='doctor-profile'>
							<div className='profile-header'>
								<button className='profile-close' onClick={() => setSelectedDoctor(null)} aria-label={t('close')}>
									<Exit width='18px' />
								</button>
								<div className='profile-img'>
									<img src={selectedDoctor.image} alt={pick(selectedDoctor.name)} />
								</div>
								<div className='profile-info'>
									<h3>{pick(selectedDoctor.name)}</h3>
									<span className='specialty'>{pick(selectedDoctor.specialty)}</span>
									<div className='meta'>
										<span>
											<User width='15px' />
											{selectedDoctor.age} {t('age')}
										</span>
										<span>
											<Time width='15px' />
											{selectedDoctor.experienceYears}+ {t('yearsOfExperience')}
										</span>
										<span>
											<Smile width='15px' />
											{selectedDoctor.rating} {t('rating')}
										</span>
									</div>
								</div>
							</div>

							<div className='profile-body'>
								<div className='section'>
									<h4>
										<Info width='18px' /> {t('brief')}
									</h4>
									<p>{pick(selectedDoctor.brief)}</p>
								</div>

								<div className='section'>
									<h4>
										<MedicalEarPhone width='18px' /> {t('study')}
									</h4>
									<p>{pick(selectedDoctor.study)}</p>
								</div>

								<div className='grid2'>
									<div className='section'>
										<h4>
											<Time width='16px' /> {t('achievements')}
										</h4>
										<ul className='achievements-list'>
											{selectedDoctor.achievements.map((a, i) => (
												<li key={i}>
													<Check width='15px' />
													{pick(a)}
												</li>
											))}
										</ul>
									</div>

									<div className='section'>
										<h4>
											<Bag width='18px' /> {t('careerPath')}
										</h4>
										<ul className='timeline'>
											{selectedDoctor.careerPath.map((c, i) => (
												<li key={i}>
													<span className='dot' />
													<div className='step'>
														<h5>{pick(c.role)}</h5>
														<span className='period'>{c.period}</span>
													</div>
												</li>
											))}
										</ul>
									</div>
								</div>

								<div className='section'>
									<h4>
										<Attach width='18px' /> {t('certificates')}
									</h4>
									<div className='certs-list'>
										{selectedDoctor.certificates.map((c) => (
											<button className='cert-card' key={c.id} onClick={() => setPreviewCert(c)}>
												<img src={c.file} alt={pick(c.title)} loading='lazy' />
												<div className='cert-overlay'>
													<span>
														<Search width='15px' /> {t('preview')}
													</span>
												</div>
												<div className='cert-footer'>
													<h5>{pick(c.title)}</h5>
													<span>{c.year}</span>
												</div>
											</button>
										))}
									</div>
								</div>
							</div>

							<div className='profile-footer'>
								<a href='/book-appointment' className='book-btn'>
									<BookAppointment width='19px' /> {t('bookNow')}
								</a>
								<button className='cancelBtn' onClick={() => setSelectedDoctor(null)}>
									{t('close')}
								</button>
							</div>
						</div>
					)}
				</Modal>
			</section>

			<Modal isOpen={!!previewCert} handleCancel={() => setPreviewCert(null)}>
				{previewCert && (
					<div className='cert-preview'>
						<img src={previewCert.file} alt={pick(previewCert.title)} />
						<div className='cert-preview-info'>
							<div>
								<h4>{pick(previewCert.title)}</h4>
								<span>{previewCert.year}</span>
							</div>
							<button className='addBtn' onClick={() => downloadImage()}>
								<Download width='17px' /> {t('download')}
							</button>
						</div>
					</div>
				)}
			</Modal>
		</>
	);
}

export default DoctorsSection;